import React, {
  lazy, Suspense, useEffect, useState,
  useContext,
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { ChildContext } from '../context';
import EsignUtils from '../../utils/esignUtils';

const EsignProvider = lazy(() => import('@americanexpress/esign-application-provider'));

const getVal = require('lodash/get');

const Esign = ({
  elementContainer,
  formSubmissionPayload,
  selectedMemberInfo,
  childProps,
  formContainer,
  loggerApiCall,
  setIsFormSubmission,
  setS3FileInfo,
  s3FileInfo,
  setAllS3FileIds,
  setS3EsignFileInfo,
  journeyId,
  payloadFields,
  setFileData,
  objectOfFunctions,
  putDataToS3,
  apiResponse,
  setApiResponse,
}) => {
  // INTL - To get the active locale
  const intl = useIntl();
  const iformIntegrationUrls = useSelector((state) => state.getIn(['config', 'memberApiUrls']));
  const contextVal = useContext(ChildContext);

  const dynamicProps = {};

  const [mergedProps, setMergedProps] = useState({});
  const [esignLoadingState, setEsignLoadingState] = useState(null);

  const esignObj = new EsignUtils(
    elementContainer,
    formContainer,
    formSubmissionPayload,
    s3FileInfo,
    setS3FileInfo,
    setAllS3FileIds,
    iformIntegrationUrls,
    contextVal,
    setS3EsignFileInfo,
    setIsFormSubmission,
    apiResponse,
    setApiResponse,
    loggerApiCall,
    objectOfFunctions,
    journeyId,
    payloadFields,
    setFileData,
    putDataToS3,
    setEsignLoadingState
  );

  useEffect(() => {
    const getPdf = async () => {
      await esignObj.loadESignPDFData(childProps);
    };
    getPdf();
  }, []);

  useEffect(() => {
    /* Creating object of functions to call dynamically via JSON */
    const objectsArr = {
      apiResponse,
      formSubmissionPayload,
      selectedMemberInfo,
    };

    /* Check button hide condition */
    if (childProps?.customTag?.childPropsStatic?.buttonHideOn) {
      const buttonHideVal = getVal(objectsArr, childProps
        ?.customTag?.childPropsStatic?.buttonHideOn.key);
      if (buttonHideVal === childProps?.customTag?.childPropsStatic?.buttonHideOn.value) {
        childProps?.customTag?.childPropsStatic.buttons.map((button) => {
          button.show = false;
          return button;
        });
      } else {
        childProps?.customTag?.childPropsStatic.buttons.map((button) => {
          button.show = true;
          return button;
        });
      }
    }

    // Dynamically Add PDF URL
    dynamicProps.pdfUrl = getVal(objectsArr, childProps?.customTag?.childPropsEval?.pdfUrl);

    // NAME is an ARRAY in the JSON
    if (childProps?.customTag?.childPropsEval?.displayUserName) {
      let arrVals = [];

      // Get all the properties which needs to be concatenated as name
      arrVals = childProps?.customTag?.childPropsEval?.displayUserName;

      if (arrVals != null && arrVals.length > 0) {
        // Temporary Array to store the results
        const userNameVals = [];
        // Iterate over each property
        arrVals.forEach((item) => {
          // Get the value
          let tempVal = getVal(objectsArr, item);
          // IN case value is not null and has length
          if (tempVal != null && tempVal.length > 0) {
            // Trim the value
            tempVal = tempVal.trim();
            // In case it still has length add it to the temp Arr
            if (tempVal.length > 0) userNameVals.push(tempVal);
          }
        });
        // Join the temporary array to create user name
        dynamicProps.displayUserName = userNameVals.join(' ');
      }
    }
    // Set merged props for ESIGN
    setMergedProps({
      ...childProps.customTag.childPropsStatic,
      ...dynamicProps,
      activeLocale: intl.locale,
      esignPdfDownloadFromApi: !!formContainer.operations
        ?.generateESignPdf?.generateEsignPdfFromApi || !!formContainer.operations
        ?.generateESignPdf?.pdfRequiredWithSignature,
      isSignatureRequiredInEventHandler: childProps.customTag.childPropsStatic
        ?.pdfSignatureOptions?.length > 0,
      loadingStatus: esignLoadingState,
      saveEventHandler: (response) => { esignObj.pdfSubmit(childProps, response); },
    });
  }, [apiResponse]);

  return (
    <Suspense
      fallback={<div>{childProps.customTag.childPropsStatic.languageData.loading}</div>}
    >
      {Object.keys(mergedProps).length > 0
        ? <EsignProvider {...mergedProps} />
        : null}
    </Suspense>
  );
};

Esign.propTypes = {
  childProps: PropTypes.objectOf(PropTypes.shape),
  elementContainer: PropTypes.objectOf(PropTypes.shape),
  formSubmissionPayload: PropTypes.objectOf(PropTypes.shape),
  selectedMemberInfo: PropTypes.objectOf(PropTypes.shape),
  formContainer: PropTypes.objectOf(PropTypes.shape),
  loggerApiCall: PropTypes.func,
  setIsFormSubmission: PropTypes.func,
  setS3FileInfo: PropTypes.func,
  s3FileInfo: PropTypes.objectOf(PropTypes.shape),
  setAllS3FileIds: PropTypes.func,
  setS3EsignFileInfo: PropTypes.func,
  s3EsignFileInfo: PropTypes.objectOf(PropTypes.shape),
  journeyId: PropTypes.string,
  payloadFields: PropTypes.objectOf(PropTypes.shape),
  setFileData: PropTypes.func,
  objectOfFunctions: PropTypes.objectOf(PropTypes.shape),
  handleContinue: PropTypes.func,
  putDataToS3: PropTypes.func,
  apiResponse: PropTypes.objectOf(PropTypes.shape),
  setApiResponse: PropTypes.func,
};

export default Esign;
